import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { DialogContent, DialogActions, Button, CircularProgress, Typography, Stack, Divider } from '@mui/material'
import Grid from '@mui/material/Grid'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import { toast } from 'react-toastify'
import { useDispatch } from 'react-redux'
import { get } from 'lodash'
import uniqBy from 'lodash/uniqBy'
import { useForm } from 'react-hook-form'

import { handleError } from 'utils/error'
import T from 'T'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'
import CancelButton from 'components/buttons/CancelButton'
import { useSendCustomEmailInvoiceListMutation } from 'api/billing/sendInvoiceList'
import { useLazyGetAccountDetailsQuery } from 'api/accounts/getAccountDetails'
import { putIsLoading } from 'middleware/actions/response'
import api from 'api'
import {
  CACHE_TAG_ACCOUNT_INVOICE_LIST,
  CACHE_TAG_ACCOUNT_RECEIVABLES_LIST,
  CACHE_TAG_ACCOUNT_RECEIVABLES_SUMMARY,
  CACHE_TAG_INVOICE_LIST,
  CACHE_TAG_ACCOUNT_EMAIL_EVENT_LIST,
  CACHE_TAG_BILLING_INVOICE_GROUP_DETAILS,
  CACHE_TAG_BILLING_INVOICE_GROUP_AGING_BUCKET_DATA,
  CACHE_TAG_BILLING_INVOICE_GROUP_EMAIL_SUMMARY_DATA,
} from 'api/cacheTagTypes'
import Loader from 'components/common/loader'
import ContactList from './email-dialog/ContactList'
import EmailRecipients from './email-dialog/EmailRecipients'
import EmailForm from './email-dialog/EmailForm'
import AttachmentsList from './email-dialog/AttachmentsList'

const TIMEOUT = 4000

/**
 * Helper to filter contacts with email and get location associations
 */
const getContactsWithEmail = (contacts, customerDetails) => {
  return contacts
    .filter(contact => !get(contact, 'deactivated', false))
    .flatMap(contact => {
      const contactMethods = get(contact, 'contactMethods', [])
      const emailMethods = uniqBy(
        contactMethods.filter(method => get(method, 'methodType') === 'Email' && get(method, 'methodValue', '')),
        method => get(method, 'methodValue')
      )

      if (emailMethods.length === 0) return []

      const locations = get(customerDetails, 'locations', [])
      const locationCount = locations.filter(location => {
        const locationContacts = get(location, 'contacts', [])
        return locationContacts.some(lc => lc.id === contact.id)
      }).length

      return emailMethods.map(emailMethod => ({
        id: `${get(contact, 'id')}-${get(emailMethod, 'methodValue')}`,
        firstName: get(contact, 'firstName', ''),
        lastName: get(contact, 'lastName', ''),
        email: get(emailMethod, 'methodValue', ''),
        billingNotifications: get(emailMethod, 'billingNotifications', false),
        serviceNotifications: get(emailMethod, 'serviceNotifications', false),
        locationCount,
      }))
    })
}

const SendInvoiceEmailDialog = ({ isOpen = false, invoice = {}, onClose, onSuccess }) => {
  const dispatch = useDispatch()
  const [sendCustomEmailInvoiceList, { isLoading: isSending }] = useSendCustomEmailInvoiceListMutation()
  const [getAccountDetails, { data: fetchedAccountDetails, isLoading: isLoadingCustomerDetails, isFetching: isFetchingCustomerDetails }] =
    useLazyGetAccountDetailsQuery()
  const [hasEmailValidationError, setHasEmailValidationError] = React.useState(false)
  const customerDetails = fetchedAccountDetails

  const invoiceNumber = get(invoice, 'invoiceNumber', '')
  const accountId = get(invoice, 'accountId', '')

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      toRecipients: [],
      ccRecipients: [],
      subject: '',
      body: '',
      attachments: [],
    },
  })

  const toRecipients = watch('toRecipients')
  const ccRecipients = watch('ccRecipients')
  const subject = watch('subject')
  const body = watch('body')
  const attachments = watch('attachments')

  useEffect(() => {
    if (isOpen && accountId) {
      getAccountDetails({ accountId, addRecent: false }, true)
    }
  }, [isOpen, accountId, getAccountDetails])

  const contacts = useMemo(() => {
    if (!customerDetails) return []

    const accountContacts = get(customerDetails, 'contacts', [])
    const locationContacts = get(customerDetails, 'locations', []).map(location => get(location, 'contacts', []))
    const mergeContacts = [...accountContacts, ...locationContacts.flat()]
    const uniqueContacts = uniqBy(mergeContacts, contact => contact.id)
    return getContactsWithEmail(uniqueContacts, customerDetails)
  }, [customerDetails])

  const handleClose = () => {
    reset()
    onClose()
  }

  useEffect(() => {
    if (isOpen) {
      if (contacts?.length > 0) {
        const billingContacts = contacts.filter(c => c.billingNotifications)
        if (billingContacts.length > 0) {
          setValue('toRecipients', billingContacts)
        }
      } else {
        setValue('toRecipients', [])
        setValue('ccRecipients', [])
        setValue('subject', '')
        setValue('body', '')
      }

      setValue('attachments', [
        {
          id: `invoice-${invoiceNumber}`,
          name: `Invoice-${invoiceNumber}.pdf`,
          type: 'invoice',
          isInvoice: true,
          size: 0,
        },
      ])
    }
  }, [isOpen, contacts, invoiceNumber, setValue])

  const handleContactToggle = contact => {
    const isSelected = toRecipients.some(c => c.id === contact.id) || ccRecipients.some(c => c.id === contact.id)

    if (isSelected) {
      setValue(
        'toRecipients',
        toRecipients.filter(c => c.id !== contact.id)
      )
      setValue(
        'ccRecipients',
        ccRecipients.filter(c => c.id !== contact.id)
      )
    } else {
      setValue('toRecipients', [...toRecipients, contact])
    }
  }

  const handleToRecipientsChange = newRecipients => {
    setValue('toRecipients', newRecipients)
  }

  const handleCcAdd = manualContact => {
    setValue('ccRecipients', [...ccRecipients, manualContact])
  }

  const handleCcRemove = contactId => {
    setValue(
      'ccRecipients',
      ccRecipients.filter(c => c.id !== contactId)
    )
  }

  const handleValidationChange = hasError => {
    setHasEmailValidationError(hasError)
  }

  const handleAttachmentsChange = newAttachments => {
    setValue('attachments', newAttachments)
  }

  const handleRefetch = (timeout = 0) =>
    setTimeout(() => {
      dispatch(
        api.util.invalidateTags([
          CACHE_TAG_ACCOUNT_RECEIVABLES_SUMMARY,
          CACHE_TAG_ACCOUNT_RECEIVABLES_LIST,
          CACHE_TAG_INVOICE_LIST,
          { type: CACHE_TAG_ACCOUNT_INVOICE_LIST, id: accountId },
          { type: CACHE_TAG_ACCOUNT_EMAIL_EVENT_LIST, id: accountId },
          CACHE_TAG_BILLING_INVOICE_GROUP_DETAILS,
          CACHE_TAG_BILLING_INVOICE_GROUP_AGING_BUCKET_DATA,
          CACHE_TAG_BILLING_INVOICE_GROUP_EMAIL_SUMMARY_DATA,
        ])
      )
    }, timeout)

  const onSubmit = async data => {
    try {
      dispatch(putIsLoading(true))

      const invoiceId = get(invoice, 'id')
      const toRecipientEmails = data.toRecipients.map(c => c.email).join(',')
      const ccRecipientEmails = data.ccRecipients
        .map(c => c.email)
        .filter(email => !data.toRecipients.some(to => to.email === email))
        .join(',')
      const attachDefaultInvoice = data.attachments.some(att => att.isInvoice)
      const fileAttachments = data.attachments.filter(att => att.file).map(att => att.file)

      await sendCustomEmailInvoiceList({
        invoiceIds: [invoiceId],
        customEmailNotification: {
          toRecipientEmails,
          ccRecipientEmails,
          subject: data.subject,
          body: data.body,
          attachDefaultInvoice,
          attachments: fileAttachments.length > 0 ? fileAttachments : null,
        },
      }).unwrap()

      toast.success(T.SEND_INVOICE_SUCCESS, {
        hideProgressBar: false,
        autoClose: TIMEOUT,
        onClose: () => {
          handleRefetch()
          if (onSuccess) onSuccess()
        },
      })

      handleClose()
    } catch (error) {
      handleError(error)
    } finally {
      dispatch(putIsLoading(false))
    }
  }

  const isFormValid = subject.trim() && body.trim() && toRecipients.length > 0 && !hasEmailValidationError && attachments.length > 0

  return (
    <HHBaseDialog open={isOpen} onClose={handleClose} maxWidth="xl" fullWidth>
      <HHDialogTitle title={T.CUSTOM_EMAIL} onClose={handleClose} />
      <DialogContent sx={{ display: 'block', flexDirection: 'column', p: 0, overflow: 'hidden', position: 'relative' }}>
        {(isLoadingCustomerDetails || isFetchingCustomerDetails) && <Loader />}
        <Grid container sx={{ flex: 1, minHeight: 0 }}>
          <Grid
            size={{ xs: 12, md: 4 }}
            sx={{
              borderRight: '1px solid',
              borderColor: 'divider',
            }}
          >
            <ContactList
              contacts={contacts}
              selectedContactIds={[...toRecipients.map(c => c.id), ...ccRecipients.map(c => c.id)]}
              onContactToggle={handleContactToggle}
              isLoading={isLoadingCustomerDetails}
            />
          </Grid>

          <Grid
            size={{ xs: 12, md: 8 }}
            sx={{ p: 2, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 240px)', overflowY: 'auto' }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <MailOutlineIcon fontSize="small" />
              <Typography variant="subtitle2">{T.EMAIL}</Typography>
            </Stack>

            <EmailRecipients
              control={control}
              errors={errors}
              contacts={contacts}
              toRecipients={toRecipients}
              ccRecipients={ccRecipients}
              onToChange={handleToRecipientsChange}
              onCcAdd={handleCcAdd}
              onCcRemove={handleCcRemove}
              onValidationChange={handleValidationChange}
            />

            <EmailForm control={control} errors={errors} />

            <AttachmentsList attachments={attachments} invoice={invoice} onAttachmentsChange={handleAttachmentsChange} />
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions>
        <CancelButton onClick={handleClose} disabled={isSending} />
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={!isFormValid || isSending}
          startIcon={isSending && <CircularProgress size={20} />}
        >
          {isSending ? T.SENDING : T.SEND}
        </Button>
      </DialogActions>
    </HHBaseDialog>
  )
}

SendInvoiceEmailDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  invoice: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
}

export default SendInvoiceEmailDialog
